import { useState, useEffect, useCallback, useRef } from "react";
import { POLLING_INTERVAL } from "../constants";

export interface UseJobListPollingReturn {
  isPolling: boolean;
  startPolling: () => unknown;
  stopPolling: () => unknown;
}

interface PollingRefState {
  timeout: NodeJS.Timeout | null;
  didFirstPoll: boolean;
  interval: number;
}

/**
 * Hook to handle automatic polling for job updates
 *
 * Features:
 * - Auto-starts when shouldBePolling is true
 * - Auto-stops when shouldBePolling is false (after first poll)
 * - Cleanup on unmount
 * - Configurable interval
 *
 * @param shouldBePolling - Whether polling should be active
 * @param onPoll - Callback function to execute on each poll
 * @param interval - Polling interval in milliseconds (default: 10 seconds)
 * @returns Polling state and control functions
 */
export function useJobListPolling(
  shouldBePolling: boolean,
  onPoll: () => Promise<void>,
  interval: number = POLLING_INTERVAL
): UseJobListPollingReturn {
  const [isPolling, setIsPolling] = useState(false);

  const pollingRef = useRef<PollingRefState>({
    timeout: null,
    didFirstPoll: false,
    interval,
  });

  const stopPolling = useCallback(() => {
    if (pollingRef.current.timeout) {
      clearInterval(pollingRef.current.timeout);
    }

    pollingRef.current.didFirstPoll = false;
    pollingRef.current.timeout = null;

    setIsPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    if (isPolling) {
      return;
    }

    const poll = async () => {
      await onPoll();
      pollingRef.current.didFirstPoll = true;
    };

    pollingRef.current.didFirstPoll = false;
    pollingRef.current.timeout = setInterval(poll, pollingRef.current.interval);

    setIsPolling(true);
  }, [isPolling, onPoll]);

  // Auto-start/stop polling based on shouldBePolling
  useEffect(() => {
    if (shouldBePolling && !isPolling) {
      startPolling();
    } else if (
      !shouldBePolling &&
      isPolling &&
      pollingRef.current.didFirstPoll
    ) {
      stopPolling();
    }
  }, [shouldBePolling, isPolling, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isPolling,
    startPolling,
    stopPolling,
  };
}
